/**
 * Real-scan regression fixtures for floor/storey detection.
 *
 * A fixture captures one detection run from the app: the stage-1 column
 * histogram grid (the exact input of the pure pipeline stages — every debug
 * surface point derives from it deterministically) plus the storeys the
 * pipeline produced. The debug panel's export button downloads it; dropping
 * the file into `__tests__/fixtures/` turns it into a regression test
 * (see fixtureReplay.spec.ts).
 *
 * Replay runs the CURRENT production code and tuning over the captured grid —
 * that is the point: after a code or tuning change, the fixtures show whether
 * real scans still resolve to the same storeys. The tuning recorded in the
 * fixture is provenance only. For now only storey floor/ceiling HEIGHTS are
 * asserted; footprints are recorded so volume checks can follow later.
 */

import * as THREE from 'three';
import type { ColumnInterval, ColumnGrid, DetectedFloorLevel } from './types';
import type { FloorDetectionTuning } from './tuning';
import { FLOOR_DETECTION_TUNING } from './tuning';
import { extractColumnSurfaces } from './surfaces';
import { extractColumnIntervals } from './intervals';
import { consensusFilterIntervals } from './roleConsensus';
import { buildRoomPatches } from './roomPatches';
import { clusterStoreys } from './storeys';

export const FLOOR_DETECTION_FIXTURE_FORMAT = 'pointorama-floor-detection-fixture';

interface FixtureVector2 {
  x: number;
  y: number;
}

interface FixtureVector3 {
  x: number;
  y: number;
  z: number;
}

interface FixtureBox {
  min: FixtureVector3;
  max: FixtureVector3;
}

/** Recorded output of one detected level — heights first, boxes for later. */
export interface FloorDetectionFixtureLevel {
  floorZ: number;
  ceilingZ: number | null;
  isSubLevel: boolean;
  footprint: FixtureBox | null;
}

export interface FloorDetectionFixture {
  format: typeof FLOOR_DETECTION_FIXTURE_FORMAT;
  version: 1;
  capturedAt: string;
  /** Tuning active at capture time. Provenance only — replay uses current production tuning. */
  tuning: FloorDetectionTuning;
  /** The drawn selection rectangle (world XY); replay aligns level footprints with it like the app does. Absent on older captures. */
  areaCorners?: FixtureVector2[];
  grid: {
    nx: number;
    ny: number;
    cellSize: number;
    binSize: number;
    binCount: number;
    originX: number;
    originY: number;
    originZ: number;
    /** Sparse: [columnIndex, [[binIndex, count], …]] for each non-empty column. */
    columns: Array<[number, Array<[number, number]>]>;
  };
  /** Levels the pipeline produced at capture time, ascending floorZ. */
  levels: FloorDetectionFixtureLevel[];
  /**
   * Ground truth: the user's manually corrected levels at export time, when
   * they differ from the raw pipeline output. The replay spec tracks these
   * as a known-unreached target (test.failing) — the moment the pipeline
   * matches them, jest flags the fixture for baseline promotion.
   */
  correctedLevels?: FloorDetectionFixtureLevel[];
}

/** Serialize levels into the fixture's recorded-level shape. */
export function serializeFixtureLevels(levels: DetectedFloorLevel[]): FloorDetectionFixtureLevel[] {
  return levels.map((level) => ({
    floorZ: level.floor.height,
    ceilingZ: level.ceiling?.height ?? null,
    isSubLevel: level.isSubLevel === true,
    footprint: level.footprint ? { min: { x: level.footprint.min.x, y: level.footprint.min.y, z: level.footprint.min.z }, max: { x: level.footprint.max.x, y: level.footprint.max.y, z: level.footprint.max.z } } : null,
  }));
}

/** Same storey heights/flags (footprints ignored — they follow the planes). */
export function fixtureLevelsEqual(a: FloorDetectionFixtureLevel[], b: FloorDetectionFixtureLevel[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((la, i) => {
    const lb = b[i];
    return la.floorZ === lb.floorZ && la.ceilingZ === lb.ceilingZ && la.isSubLevel === lb.isSubLevel;
  });
}

/** Serialize a finished detection run (grid + levels) into a fixture. */
export function buildFloorDetectionFixture(grid: ColumnGrid, levels: DetectedFloorLevel[], tuning: FloorDetectionTuning, areaCorners?: THREE.Vector2[]): FloorDetectionFixture {
  const columns: Array<[number, Array<[number, number]>]> = [];
  for (let column = 0; column < grid.columns.length; column++) {
    const bins = grid.columns[column];
    if (!bins) continue;
    const entries: Array<[number, number]> = [];
    for (let i = 0; i < bins.length; i++) {
      if (bins[i] > 0) entries.push([i, bins[i]]);
    }
    if (entries.length > 0) columns.push([column, entries]);
  }

  return {
    format: FLOOR_DETECTION_FIXTURE_FORMAT,
    version: 1,
    capturedAt: new Date().toISOString(),
    tuning,
    areaCorners: areaCorners?.map((c) => ({ x: c.x, y: c.y })),
    grid: {
      nx: grid.nx,
      ny: grid.ny,
      cellSize: grid.cellSize,
      binSize: grid.binSize,
      binCount: grid.binCount,
      originX: grid.originX,
      originY: grid.originY,
      originZ: grid.originZ,
      columns,
    },
    levels: serializeFixtureLevels(levels),
  };
}

/**
 * Re-run pipeline stages 2–5 (surfaces → intervals → role consensus →
 * patches → storeys) over a fixture's captured histogram grid. Keep the
 * stage sequence in sync with `detectStoreys` — this is the same pipeline
 * minus scene access, progress and debug-group building.
 */
export function replayFloorDetectionFixture(fixture: FloorDetectionFixture, tuning: FloorDetectionTuning = FLOOR_DETECTION_TUNING): DetectedFloorLevel[] {
  const { nx, ny, cellSize, binSize, binCount, originX, originY, originZ } = fixture.grid;

  const columns = new Array<Uint16Array | null>(nx * ny).fill(null);
  for (const [columnIndex, entries] of fixture.grid.columns) {
    const bins = new Uint16Array(binCount);
    for (const [binIndex, count] of entries) bins[binIndex] = count;
    columns[columnIndex] = bins;
  }

  const rawIntervalsByColumn: Array<ColumnInterval[] | null> = new Array(columns.length).fill(null);
  for (let column = 0; column < columns.length; column++) {
    const bins = columns[column];
    if (!bins) continue;
    const surfaces = extractColumnSurfaces(bins, binSize, originZ, tuning);
    if (surfaces.length === 0) continue;
    const { intervals } = extractColumnIntervals(surfaces, bins, binSize, originZ, tuning);
    if (intervals.length > 0) rawIntervalsByColumn[column] = intervals;
  }

  const { filtered: intervalsByColumn } = consensusFilterIntervals(rawIntervalsByColumn, nx, ny);
  const patches = buildRoomPatches(intervalsByColumn, nx, ny, cellSize, originX, originY, tuning);
  const frame = fixture.areaCorners && fixture.areaCorners.length >= 4 ? { areaCorners: fixture.areaCorners.map((c) => new THREE.Vector2(c.x, c.y)), grid: { nx, cellSize, originX, originY } } : undefined;
  return clusterStoreys(patches, tuning, frame).levels;
}
